import { styledOmitCustomProps } from '@braskai/ui-kit';

import { alpha, Box, ButtonBase, keyframes, styled, Theme } from '@mui/material';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const dialogIn = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }`;
const sheetIn = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; }`;

type CompactProps = { $isCompact: boolean };
type HighlightProps = { $isHighlighted: boolean };

const focusVisibleStyles = (theme: Theme) => ({
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary[600]}`, outlineOffset: 2 },
});

export const SOverlay = styled(
  Box,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  alignItems: $isCompact ? 'stretch' : 'center',
  animation: `${fadeIn} 200ms ease-out`,
  backdropFilter: 'blur(14px)',
  background: alpha(theme.palette.grey[900], 0.36),
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  outline: 'none',
  padding: $isCompact ? '48px 16px 24px' : 24,
  WebkitBackdropFilter: 'blur(14px)',
  width: '100%',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

export const SDialog = styled(
  Box,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  animation: `${$isCompact ? sheetIn : dialogIn} 220ms ease-out`,
  alignSelf: 'center',
  background: theme.palette.common.white,
  borderRadius: 24,
  boxShadow: $isCompact
    ? '0 24px 48px -12px rgba(16, 24, 40, 0.28)'
    : '0 32px 64px -12px rgba(16, 24, 40, 0.24), 0 8px 16px -8px rgba(16, 24, 40, 0.08)',
  boxSizing: 'border-box',
  color: theme.palette.grey[900],
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100%',
  maxWidth: $isCompact ? 480 : '100%',
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  ...($isCompact ? { height: '100%', width: '100%' } : { width: 1080 }),
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

export const SCloseButton = styled(
  ButtonBase,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  ...focusVisibleStyles(theme),
  ...($isCompact
    ? {
        '&:active': { background: theme.palette.grey[200] },
        background: theme.palette.grey[100],
        borderRadius: 12,
        color: theme.palette.grey[700],
        flex: 'none',
        height: 44,
        margin: '-6px -8px 0 0',
        width: 44,
      }
    : {
        '&:hover': { background: theme.palette.grey[100], color: theme.palette.grey[900] },
        borderRadius: 10,
        color: theme.palette.grey[500],
        height: 40,
        position: 'absolute',
        right: 16,
        top: 16,
        transition: 'background 100ms',
        width: 40,
      }),
}));

export const SPricingChip = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  alignSelf: 'flex-start',
  background: theme.palette.primary[50],
  borderRadius: 999,
  color: theme.palette.primary[700],
  display: 'inline-flex',
  gap: 6,
  height: 24,
  padding: '0 10px',
  ...theme.typography['caption-1-md'],
}));

export const SToggle = styled(
  Box,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 999,
  boxShadow: theme.customShadows.md,
  display: $isCompact ? 'grid' : 'flex',
  flex: 'none',
  gap: 2,
  gridTemplateColumns: '1fr 1fr',
  padding: 4,
}));

export const SToggleTab = styled(
  ButtonBase,
  styledOmitCustomProps,
)<{ $isSelected: boolean } & CompactProps>(({ $isCompact, $isSelected, theme }) => ({
  ...focusVisibleStyles(theme),
  background: $isSelected ? theme.palette.primary[600] : 'transparent',
  borderRadius: 999,
  boxShadow: $isSelected ? `0 0 0 2px ${theme.palette.primary[150]}` : 'none',
  color: $isSelected ? theme.palette.common.white : theme.palette.grey[900],
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  height: $isCompact ? 40 : 36,
  lineHeight: '20px',
  padding: $isCompact ? 0 : '0 20px',
}));

export const SCard = styled(
  Box,
  styledOmitCustomProps,
)<CompactProps & HighlightProps>(({ $isCompact, $isHighlighted, theme }) => {
  const padding = $isCompact ? 20 : 24;

  return {
    background: theme.palette.common.white,
    border: $isHighlighted ? `2px solid ${theme.palette.primary[600]}` : `1px solid ${theme.palette.primary[100]}`,
    borderRadius: 20,
    boxShadow: $isHighlighted ? `0 0 0 4px ${theme.palette.primary[100]}, ${theme.customShadows.lg}` : 'none',
    boxSizing: 'border-box',
    padding: $isHighlighted ? padding - 1 : padding,
    position: 'relative',
    ...($isCompact
      ? { display: 'flex', flexDirection: 'column', minWidth: 0 }
      : { display: 'grid', gridRow: 'span 6', gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: 'subgrid' }),
  };
});

export const SEnterpriseCard = styled(
  Box,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  background: theme.palette.primary[25],
  border: `1px solid ${theme.palette.primary[100]}`,
  borderRadius: 20,
  boxSizing: 'border-box',
  color: theme.palette.grey[900],
  padding: $isCompact ? 20 : 24,
  ...($isCompact
    ? { display: 'flex', flexDirection: 'column', minWidth: 0 }
    : { display: 'grid', gridRow: 'span 6', gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: 'subgrid' }),
}));

export const SSavingsBadge = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.primary[50],
  border: `1px solid ${theme.palette.primary[150]}`,
  borderRadius: 999,
  boxSizing: 'border-box',
  color: theme.palette.primary[700],
  display: 'inline-flex',
  maxWidth: 'calc(100% - 32px)',
  minHeight: 26,
  padding: '4px 10px',
  position: 'absolute',
  right: 16,
  textAlign: 'center',
  textWrap: 'balance',
  top: 0,
  transform: 'translateY(calc(1px - 50%))',
  ...theme.typography['caption-1-md'],
}));

export const SMinutes = styled(
  Box,
  styledOmitCustomProps,
)<{ $isEnterprise: boolean }>(({ $isEnterprise, theme }) => ({
  alignItems: 'center',
  background: $isEnterprise ? theme.palette.common.white : theme.palette.primary[50],
  border: $isEnterprise ? `1px solid ${theme.palette.primary[100]}` : 'none',
  borderRadius: 12,
  boxSizing: 'border-box',
  color: theme.palette.grey[900],
  display: 'flex',
  gap: 8,
  height: $isEnterprise ? 46 : 44,
  justifyContent: 'center',
  padding: '0 12px',
  ...theme.typography['body-2-md'],
  '& svg': { color: theme.palette.primary[600], flex: 'none' },
}));

export const SFeatureList = styled(
  'ul',
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  listStyle: 'none',
  margin: 0,
  padding: $isCompact ? '14px 0 18px' : '16px 0 20px',
}));

export const SFeature = styled('li')(({ theme }) => ({
  alignItems: 'start',
  color: theme.palette.grey[700],
  display: 'grid',
  gap: 10,
  gridTemplateColumns: '12px 1fr',
  ...theme.typography['body-2'],
  '& svg': { color: theme.palette.primary[600], height: 9, marginTop: 6, width: 12 },
}));

type CtaVariant = 'outlined' | 'primary' | 'secondary';

export const SCta = styled(
  ButtonBase,
  styledOmitCustomProps,
)<{ $variant: CtaVariant } & CompactProps>(({ $isCompact, $variant, theme }) => {
  const variants = {
    outlined: {
      active: theme.palette.grey[100],
      background: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[300]}`,
      boxShadow: theme.customShadows.xs,
      color: theme.palette.grey[900],
      hover: theme.palette.grey[50],
    },
    primary: {
      active: theme.palette.primary[800],
      background: theme.palette.primary[600],
      border: '1px solid transparent',
      boxShadow: 'none',
      color: theme.palette.common.white,
      hover: theme.palette.primary[700],
    },
    secondary: {
      active: theme.palette.grey[300],
      background: theme.palette.grey[100],
      border: '1px solid transparent',
      boxShadow: 'none',
      color: theme.palette.grey[900],
      hover: theme.palette.grey[200],
    },
  }[$variant];

  return {
    ...focusVisibleStyles(theme),
    '&:active': { background: variants.active },
    '&:hover:not(:active)': { background: variants.hover },
    '&.Mui-disabled': { opacity: 0.4 },
    background: variants.background,
    border: variants.border,
    borderRadius: 8,
    boxShadow: variants.boxShadow,
    boxSizing: 'border-box',
    color: variants.color,
    fontFamily: 'inherit',
    fontSize: $isCompact ? 16 : 14,
    fontWeight: 500,
    gap: 8,
    lineHeight: $isCompact ? '22px' : '20px',
    minHeight: $isCompact ? 44 : 40,
    padding: $isCompact ? '10px 18px' : '9px 14px',
    textAlign: 'center',
    transition: 'background 120ms ease',
    width: '100%',
  };
});

export const SFooterButton = styled(
  ButtonBase,
  styledOmitCustomProps,
)<CompactProps>(({ $isCompact, theme }) => ({
  ...focusVisibleStyles(theme),
  ...($isCompact
    ? {
        '&:active': { background: theme.palette.grey[100] },
        borderRadius: 12,
        color: theme.palette.grey[700],
        fontFamily: 'inherit',
        fontSize: 15,
        fontWeight: 500,
        height: 48,
        lineHeight: '20px',
        width: '100%',
      }
    : {
        '&:hover': { background: theme.palette.grey[100], color: theme.palette.grey[900] },
        borderRadius: 10,
        color: theme.palette.grey[600],
        height: 40,
        padding: '0 16px',
        transition: 'background 100ms',
        ...theme.typography['body-2-md'],
      }),
}));
